import { useState } from 'react';
import { ArrowDown, ArrowUp, BookCopy, Ellipsis, Eye, Pencil, Trash2 } from 'lucide-react';

import { Button } from '~/shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '~/shadcn/components/ui/Card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '~/shadcn/components/ui/DropdownMenu';
import { cn } from '~/shadcn/lib/utils';

/**
 * An editable list of items with add, edit, duplicate, reorder, and remove functionality.
 *
 * @param {Object} props
 * @param {Object} props.defaultItem - The default item template used when adding new items
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {React.ComponentType} props.ListItemForm - Component to render the edit form for a list item
 * @param {React.ComponentType} props.ListItemPreview - Component to render the preview of a list item
 * @param {Array} props.listItems - Array of items to display in the list
 * @param {Function} props.onChange - Callback function called when the list is modified
 * @param {Object} props.slice - Additional data passed to child components
 * @returns {JSX.Element} The EditableList component
 */
function EditableList({ defaultItem, disabled, ListItemForm, ListItemPreview, listItems, onChange, slice }) {
    const [editingIndices, setEditingIndices] = useState(
        listItems.reduce((acc, _, index) => ({ ...acc, [index]: false }), {})
    );

    const addItem = () => {
        const newListItem = { ...defaultItem };
        const updatedListItems = [...listItems, newListItem];
        onChange(updatedListItems);
        setEditingIndices((prev) => ({ ...prev, [updatedListItems.length - 1]: true }));
    };

    const changeItem = (listItemIndex, updates) => {
        const updatedItems = [...listItems];
        updatedItems[listItemIndex] = {
            ...updatedItems[listItemIndex],
            ...updates,
        };
        onChange(updatedItems);
    };

    const duplicateItem = (listItemIndex) => {
        const updatedListItems = [...listItems];
        updatedListItems.splice(listItemIndex + 1, 0, { ...updatedListItems[listItemIndex] });
        onChange(updatedListItems);

        const updatedEditingIndices = updatedListItems.reduce((acc, _, index) => {
            if (index === listItemIndex + 1) {
                return { ...acc, [index]: true };
            }
            const oldIndex = index > listItemIndex ? index - 1 : index;
            return { ...acc, [index]: editingIndices[oldIndex] || false };
        }, {});
        setEditingIndices(updatedEditingIndices);
    };

    const toggleItemEditing = (listItemIndex) => {
        setEditingIndices((prev) => ({ ...prev, [listItemIndex]: !prev[listItemIndex] }));
    };

    const reorderItems = (oldItemIndex, newItemIndex) => {
        const updatedListItems = [...listItems];
        const [movedItem] = updatedListItems.splice(oldItemIndex, 1);
        updatedListItems.splice(newItemIndex, 0, movedItem);
        onChange(updatedListItems);

        setEditingIndices((prev) => ({
            ...prev,
            [oldItemIndex]: prev[newItemIndex],
            [newItemIndex]: prev[oldItemIndex],
        }));
    };

    const handleRemoveItem = (listItemIndex) => {
        const updatedListItems = listItems.filter((_, i) => i !== listItemIndex);
        onChange(updatedListItems);

        const updatedEditingIndices = updatedListItems.reduce((acc, _, index) => {
            const oldIndex = index >= listItemIndex ? index + 1 : index;
            return { ...acc, [index]: editingIndices[oldIndex] || false };
        }, {});
        setEditingIndices(updatedEditingIndices);
    };

    const handleInvalidInput = (index) => {
        setEditingIndices((prev) => ({ ...prev, [index]: true }));
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <p className="text-md text-muted-foreground font-medium">Banner Items ({listItems.length})</p>
                {listItems.map((listItem, index) => (
                    <Card className="gap-3" key={index}>
                        <CardHeader className="gap-0">
                            <CardTitle className="flex justify-between items-center text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">{index + 1}.</span> Banner Item
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => toggleItemEditing(index)}
                                        type="button"
                                    >
                                        {editingIndices[index] ? <Eye /> : <Pencil />}
                                    </Button>
                                    <ListItemDropdown
                                        index={index}
                                        listItems={listItems}
                                        onDuplicate={() => duplicateItem(index)}
                                        onMoveUp={() => reorderItems(index, index - 1)}
                                        onMoveDown={() => reorderItems(index, index + 1)}
                                        onRemove={() => handleRemoveItem(index)}
                                        disabled={disabled}
                                    />
                                </div>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div
                                className={cn(
                                    'flex flex-col gap-6',
                                    editingIndices[index] ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'
                                )}
                            >
                                <ListItemForm
                                    index={index}
                                    listItem={listItem}
                                    onChange={(updates) => changeItem(index, updates)}
                                    onInvalidInput={() => handleInvalidInput(index)}
                                    slice={slice}
                                    disabled={disabled}
                                />
                            </div>

                            {!editingIndices[index] && (
                                <div className="grid grid-cols-[max-content_1fr] gap-y-1 gap-x-8 items-baseline">
                                    <ListItemPreview listItem={listItem} />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
            <Button className="self-end" variant="default" onClick={addItem} type="button">
                Add Banner Item
            </Button>
        </div>
    );
}

/**
 * A dropdown menu for item actions including move up/down, duplicate, and remove.
 *
 * @param {Object} props - The component props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {number} props.index - The index of the current list item
 * @param {Array} props.listItems - The array of all list items
 * @param {Function} props.onDuplicate - Callback function to duplicate the current item
 * @param {Function} props.onMoveDown - Callback function to move the item down in the list
 * @param {Function} props.onMoveUp - Callback function to move the item up in the list
 * @param {Function} props.onRemove - Callback function to remove the current item
 * @returns {JSX.Element} The ListItemDropdown component
 */
function ListItemDropdown({ disabled, index, listItems, onDuplicate, onMoveDown, onMoveUp, onRemove }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="data-[state=open]:bg-muted data-[state=open]:hover:bg-muted"
                    disabled={disabled}
                >
                    <Ellipsis />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                    onClick={onMoveUp}
                    disabled={index === 0}
                    className={index === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                >
                    <ArrowUp /> Move Up
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={onMoveDown}
                    disabled={index === listItems.length - 1}
                    className={index === listItems.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}
                >
                    <ArrowDown /> Move Down
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicate}>
                    <BookCopy />
                    Duplicate
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={onRemove}>
                    <Trash2 />
                    Remove
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/**
 * A component to display a detail field of a list item with different display types.
 *
 * @param {Object} props - The component props
 * @param {string} props.label - The label for the detail field
 * @param {string|Object} props.value - The value to display. For image type, should be an object with imageUrl and imagePreview
 * @param {('text'|'bg-color'|'image')} [props.type='text'] - The type of detail to display
 * @returns {JSX.Element} The ListItemDetail component
 */
export function ListItemDetail({ label, value, type = 'text' }) {
    if (type === 'bg-color') {
        return (
            <>
                <p className="text-xs font-medium text-muted-foreground transform -translate-y-1">{label}</p>
                <div className="flex items-center gap-2">
                    <div className="size-4 rounded border" style={{ backgroundColor: value || '#FFFFFF' }} />
                    <p className="text-sm text-left">
                        {value || <span className="italic text-muted-foreground">None</span>}
                    </p>
                </div>
            </>
        );
    }

    if (type === 'image') {
        return (
            <>
                <p className={cn('text-xs font-medium text-muted-foreground', value.imageUrl && 'mb-0.5')}>{label}</p>
                <div>
                    {value.imagePreview && (
                        <img className="max-w-60 max-h-20 rounded border" src={value.imagePreview} />
                    )}
                    {value.imageUrl && !value.imagePreview && (
                        <img
                            className="max-w-60 max-h-20 rounded border"
                            src={`https://img-cdn.misfitsmarket.com/${value.imageUrl}`}
                        />
                    )}
                    {!value.imagePreview && !value.imageUrl && (
                        <span className="italic text-muted-foreground">None</span>
                    )}
                </div>
            </>
        );
    }

    return (
        <>
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className="text-sm">{value || <span className="italic text-muted-foreground">None</span>}</p>
        </>
    );
}

export default EditableList;
